/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Select,
  Textarea,
  Text,
  useToast,
  VStack,
  Alert,
  AlertIcon,
  Box,
} from "@chakra-ui/react";
import api from "utils/api";

export default function IadeAlModal({ isOpen, onClose, aracData, onSuccess }) {
  const toast = useToast();
  const [durum, setDurum] = useState("hazir");
  const [saving, setSaving] = useState(false);
  const [notMetni, setNotMetni] = useState("");

  useEffect(() => {
    if (isOpen) {
      setDurum("hazir");
      setNotMetni("");
    }
  }, [isOpen]);

  const handleSave = async () => {
    if (!aracData?.id) {
      toast({
        title: "Hata",
        description: "Cihaz bilgisi bulunamadı.",
        status: "error",
        duration: 3000,
      });
      return;
    }

    const confirmMessage = `Bu cihazı iade almak istediğinize emin misiniz?\n\nPlaka: ${aracData.plaka}\nIMEI: ${aracData.imei}\nStok Durumu: ${durum === "hazir" ? "Hazır" : "Arıza"}\n\nBu işlem:\n- Cihazı stok yönetimine ekleyecek\n- Traccar'da pasifleştirecek\n- Sistemde görünmez yapacak`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setSaving(true);
      const res = await api.post(`/api/devices/${aracData.id}/iade-al`, {
        durum: durum,
        not_metni: notMetni || null,
      });
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data?.detail || "İade alınamadı.");
      }

      toast({
        title: "İade Alındı",
        description: `Cihaz başarıyla iade alındı. Stok yönetimine "${durum === "hazir" ? "Hazır" : "Arıza"}" durumunda eklendi.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      onSuccess?.();
      onClose();
    } catch (err) {
      toast({
        title: "Hata",
        description: err.message,
        status: "error",
        duration: 4000,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered motionPreset="slideInBottom">
      <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="2xl" boxShadow="xl" m={4} maxH="90vh" overflow="hidden" bg="white" p={1}>
        {/* Header */}
        <Box bg="white" p={4} borderBottom="1px solid" borderColor="gray.200">
          <ModalCloseButton size="sm" />
          <VStack spacing={1} align="start">
            <Text fontSize="lg" fontWeight="medium" color="gray.800">
              İade Al
            </Text>
            <Text fontSize="xs" color="gray.400">
              {aracData?.plaka || "Cihaz"}
            </Text>
          </VStack>
        </Box>
        <ModalBody px={6} py={4}>
          <VStack spacing={4} align="stretch">
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">
                Bu işlem geri alınamaz. Cihaz stok yönetimine eklenecek ve sistemde görünmez olacak.
              </Text>
            </Alert>

            <FormControl>
              <FormLabel fontSize="sm" color="gray.600">Cihaz Bilgileri</FormLabel>
              <VStack align="start" spacing={1} mt={2}>
                <Text fontSize="sm" color="gray.600">
                  <strong>Plaka:</strong> {aracData?.plaka || "-"}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  <strong>IMEI:</strong> {aracData?.imei || "-"}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  <strong>Cihaz Tipi:</strong> {aracData?.cihaz_tipi || "-"}
                </Text>
              </VStack>
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm" color="gray.600">Stok Durumu</FormLabel>
              <Select
                variant="flushed"
                size="sm"
                value={durum}
                onChange={(e) => setDurum(e.target.value)}
              >
                <option value="hazir">Hazır</option>
                <option value="ariza">Arıza</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm" color="gray.600">İade Notu (Opsiyonel)</FormLabel>
              <Textarea
                variant="flushed"
                size="sm"
                value={notMetni}
                onChange={(e) => setNotMetni(e.target.value)}
                placeholder="Neden iade alındı? Örn: Müşteri iptali / cihaz arızası / sözleşme bitti"
                rows={3}
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter bg="white" px={6} py={4}>
          <Button variant="ghost" mr={3} onClick={onClose} size="sm">
            İptal
          </Button>
          <Button colorScheme="red" onClick={handleSave} isLoading={saving} size="sm">
            İade Al
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

